import { AGENT_EVENTS, gracefulChunkingWithLimit } from "../utils/agent-utils";
import { sanitizeAIMessage } from "../utils/chat-ai-message";
import {
  createThread,
  sendAudioTranscription,
  sendThreadPrompt,
  getCookieToken,
  OPENAI_AGENT_URL
} from "../utils/openAI-utils";
import AudioManager from "./Agents/AudioManagerAI";
import voiceSynthesizer from "./VoiceSynthetizer";

let isCancelled = false;

export type Agent = {
  apiKey: string;
  assistantId?: string;
  model?: string;
  endpoint?: string;
  inputType: string;
  encrypted: boolean;
  enableOnProximity: boolean;
  proximityRadius: number;
  name: string;
  isAi?: boolean;
  id: string;
  objectUuid: string; // Add objectUuid
  el?: any;
  threadId?: string;
  welcomeText?: string;
  voiceSpeechStrategy?: "OpenAI" | "WebAPI" | "Silent";
  audioBuffers: any;
  welcomeTextUrl?: string;
  currentAudio: HTMLAudioElement;
  provider?: string;
  systemPrompt?: string;
  currentPromptId?: number;
};

class AIAgents {
  private static instance: AIAgents;
  public status: "talking" | "listening" | "idle" | "thinking";
  public currentPromptId?: number;
  private agents: Array<Agent>;
  private errorOpenAIAudio: string;
  private audioManager = new AudioManager();
  private bufferKey = 0;
  // Queue to hold pending prompts
  // Queue to hold pending prompts with additional parameters
  private promptQueue: Array<{
    agent: Agent;
    message: string;
    enableSpeech: boolean;
    messageId?: number;
    resolve: () => void;
    reject: (error: any) => void;
  }> = [];
  // Flag to indicate if a prompt is currently being processed
  private isProcessing: boolean = false;

  private currentAbortController: AbortController | null = null;

  private constructor() {
    this.agents = [];
    this.status = "idle";
    this.errorOpenAIAudio = "https://nextrealitydigital.blob.core.windows.net/nrd/metaverse/error-handler-npc.mp3";
  }

  public static getInstance(): AIAgents {
    if (!AIAgents.instance) {
      AIAgents.instance = new AIAgents();
    }

    return AIAgents.instance;
  }

  private generateRandomName(): string {
    const now = new Date();
    const timestamp = now.getTime(); // Current time in milliseconds
    return `Agent_${timestamp}`;
  }

  public generateRandomId(): string {
    const randomPart = Math.random().toString(36).substr(2, 9); // Generate a random alphanumeric string
    const timestamp = Date.now().toString(36); // Convert current timestamp to base36
    return `ID_${timestamp}_${randomPart}`; // Combine timestamp and random part
  }

  public async playAgentAudio(id: string, message: any, group: any) {
    const agent = this.getAgentById(id);

    if (agent) {
      await this.cancelAllPlay(agent, false);
      if (agent.voiceSpeechStrategy === "WebAPI") {
        const textMessage = message?.messages[0].body;
        await new Promise(resolve => setTimeout(resolve, 100));
        this.playAgentWebAPI(agent, textMessage, message.id, group.id);

        window.APP.messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message-edit", {
            detail: {
              type: "private-chat",
              body: { play: false, playable: true, loadingAudio: true },
              messageId: message.id,
              groupId: group.id
            }
          })
        );
      } else if (agent.voiceSpeechStrategy === "OpenAI") {
        this.playAgentAudioBuffer(agent, message);

        window.APP.messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message-edit", {
            detail: {
              type: "private-chat",
              body: { play: true },
              messageId: message.id,
              groupId: group.id
            }
          })
        );
      }
    }
  }

  private async playAgentAudioBuffer(agent: Agent, message: any) {
    const bufferKey = message.messages[0]?.audio?.bufferKey;
    if (bufferKey !== undefined && agent.audioBuffers) {
      const audioBuffer = agent.audioBuffers[bufferKey];
      if (audioBuffer) {
        this.handleBufferAudio(agent, audioBuffer, undefined, bufferKey);
      } else {
        console.error("there are no available buffer");
      }
    }
  }

  private async handleAudioUrl(url: string, agent: Agent) {
    this.playAudioUrl(agent, url);
    this.handleAudioEvents(agent);
  }

  private handleAudioEvents(agent: Agent, bufferKey?: number) {
    let audioEl = agent.currentAudio;

    const handlePlaying = () => {
      this.dispatchStartSpeaking(agent);
    };

    // Clean up previous listeners if any
    audioEl.removeEventListener("playing", handlePlaying);
    audioEl.onended = null;

    audioEl.addEventListener("playing", handlePlaying, { once: true });

    const endAudio = () => {
      (window as any).agentDebug = agent;
      this.handleFinishEndAudio(agent);
      audioEl.removeEventListener("playing", handlePlaying);
    };

    audioEl.addEventListener("ended", endAudio, { once: true });
  }

  private async handleBufferAudio(agent: Agent, audioData: any, timestamp?: number, bufferKey?: number) {
    await this.audioManager.playStoredAudio(agent, audioData, timestamp);
    this.handleAudioEvents(agent, bufferKey);
  }

  public playAgentWebAPI(agent: Agent, textMessage: string, messageId?: number, groupId?: string) {
    voiceSynthesizer.agent = agent;
    voiceSynthesizer.addToQueue(textMessage, true, messageId, groupId);
  }

  public sendThreadMessageAI({
    id,
    el,
    message,
    receiver,
    audio = false,
    play = false,
    loadingAudio = false,
    playable = undefined
  }: {
    id?: any;
    el: any;
    message: any;
    receiver: any;
    audio?: any;
    play?: boolean;
    loadingAudio?: boolean;
    playable?: boolean;
  }) {
    el.dispatchEvent(
      new CustomEvent("ai-message", {
        detail: { type: "private-chat", body: message, receiver, audio, id, play, loadingAudio, playable }
      })
    );
  }

  public registerAgent(agent: {
    name: string;
    apiKey: string;
    assistantId?: string;
    model?: string;
    endpoint?: string;
    inputType: string;
    encrypted: boolean;
    enableOnProximity: boolean;
    proximityRadius: number;
    el?: HTMLElement;
    id: string;
    objectUuid: string; // Add objectUuid to signature
    provider: string; // Add provider to signature
    welcomeText?: string;
    welcomeTextUrl?: string;
    voiceSpeechStrategy: "OpenAI" | "WebAPI" | "Silent";
    systemPrompt?: string; // Add systemPrompt to signature
  }): void {
    const {
      apiKey,
      objectUuid, // Destructure objectUuid
      systemPrompt, // Destructure systemPrompt
      assistantId,
      model,
      endpoint,
      inputType,
      encrypted,
      enableOnProximity,
      proximityRadius,
      el,
      name,
      id,
      welcomeText,
      welcomeTextUrl,
      voiceSpeechStrategy,
      provider
    } = agent;

    // Call the registration endpoint
    this.registerAgentEndpoint(objectUuid, provider, assistantId, model, endpoint, apiKey, systemPrompt);

    this.agents.push({
      name: name || this.generateRandomName(),
      id,
      objectUuid, // Store objectUuid
      apiKey,
      isAi: true,
      assistantId,
      model,
      endpoint,
      inputType,
      encrypted,
      enableOnProximity,
      el,
      proximityRadius,
      welcomeText,
      voiceSpeechStrategy,
      audioBuffers: {},
      welcomeTextUrl,
      currentAudio: new Audio(),
      provider,
      systemPrompt: systemPrompt || "" // Add systemPrompt to agent object
    });
  }

  // Method to register the agent with the backend
  private async registerAgentEndpoint(
    uuid: string,
    provider: string,
    assistantId: string = "",
    model: string = "",
    endpoint: string = "",
    apiKey: string,
    systemPrompt: string = ""
  ): Promise<void> {
    const cookie = getCookieToken();
    const url = `${OPENAI_AGENT_URL}/agent`; // Use the correct endpoint path

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookie}`,
          Accept: "application/json"
        },
        body: JSON.stringify({
          uuid: uuid, // Send objectUuid as uuid
          provider: provider,
          assistantId,
          model,
          endpoint,
          apiKey: apiKey,
          systemPrompt: systemPrompt
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Failed to register agent ${uuid}: ${response.status}`, errorData);
        // Handle error appropriately, maybe throw or log
      }
    } catch (error) {
      console.error(`Error registering agent ${uuid}:`, error);
      // Handle network or other errors
    }
  }

  public updateAgent(agent: Agent) {
    const agentIndex = this.agents.findIndex(a => a.id === agent.id);
    this.agents[agentIndex] = agent;
  }

  public getAgents() {
    return this.agents;
  }

  public getAgentById(id: any) {
    const agent = this.agents.find(agent => agent.id == id);
    return agent || null; // Return null if not found
  }

  public setMyLocalMessageTranscribed({
    messageTranscribed,
    receiver,
    mySessionId
  }: {
    messageTranscribed: any;
    receiver: any;
    mySessionId: any;
  }) {
    window.APP.messageDispatch.dispatchEvent(
      new CustomEvent("ai-local-message", {
        detail: {
          type: "private-chat",
          body: {
            message: messageTranscribed,
            receiver,
            sender: "You",
            sent: true,
            privacyType: "private-chat",
            senderSessionId: mySessionId,
            playable: true
          }
        }
      })
    );
  }

  public async createAgentThread(id: any) {
    const agent = this.getAgentById(id);
    const threadId = await createThread(agent?.apiKey);
    if (agent) {
      agent.threadId = threadId;
    }
  }

  public setAILoadingMessage(receiver: any) {
    const agent = this.getAgentById(receiver);
    window.APP.messageDispatch.dispatchEvent(
      new CustomEvent("ai-local-message", {
        detail: {
          type: "private-chat",
          body: {
            message: "",
            receiver: receiver,
            sender: agent?.name,
            loading: true,
            privacyType: "private-chat",
            playable: agent?.voiceSpeechStrategy !== "Silent"
          }
        }
      })
    );
  }

  public cancelPreviousAudiosPlayStatus() {
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
  }

  public handleErrorTranscription(agent: Agent) {
    this.cancelPreviousAudiosPlayStatus();

    if (agent.voiceSpeechStrategy === "WebAPI") {
      voiceSynthesizer.agent = agent;
      voiceSynthesizer.stop();
      voiceSynthesizer.isSpeaking = false;
      voiceSynthesizer.queue = [];
      voiceSynthesizer.addToQueue(
        "Apologize, There was an unexpected issue processing your audio. Please try again.",
        true
      );
    } else if (agent.voiceSpeechStrategy === "OpenAI") {
      this.handleAudioUrl(this.errorOpenAIAudio, agent);
    }
  }

  public dispatchStartSpeaking(agent?: Agent) {
    if (agent) {
      agent?.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_SPEAK));
    }
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_SPEAK));
  }

  public dispatchAgentTalking(agent: Agent, amplitude: number) {
    if (agent) {
      agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_TALKING_FRAME, { detail: { amplitude } }));
    }

    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_TALKING_FRAME, { detail: { amplitude } }));
  }

  public handleFinishEndAudio(agent: Agent) {
    if (agent) {
      agent?.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-end-audio"));
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));
  }

  private unlockSpeechSynthesis() {
    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance("");
      utterance.onend = resolve;
      utterance.onerror = reject;
      window.speechSynthesis.speak(utterance);
    });
  }

  playBlankAudioAll() {
    aiAgents.agents.forEach(agent => {
      if (agent.voiceSpeechStrategy === "WebAPI") {
        this.unlockSpeechSynthesis();
      }
      agent.currentAudio.src = "#";
      agent.currentAudio.autoplay = true;
    });
  }

  public dispatchThinkingAI() {
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_THINKING));
  }

  public dispatchFinishThinkingAI() {
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
  }
  public async speakAndSendPrompt({ agent, message }: { agent: Agent; message: string }) {
    aiAgents.setAILoadingMessage(agent.id);
    await this.sendPrompt({ agent, message, enableSpeech: true });
  }

  /**
   * Enqueue a prompt to be sent with additional parameters.
   * @param agent The agent to send the prompt.
   * @param message The message to send.
   * @param enableSpeech Whether to enable speech for this prompt.
   * @param messageId Optional message identifier.
   * @returns A Promise that resolves when the prompt has been processed.
   */
  // public speakAndSendPromptQueue({
  //   agent,
  //   message,
  //   enableSpeech = true,
  //   messageId,
  // }: {
  //   agent: Agent;
  //   message: string;
  //   enableSpeech?: boolean;
  //   messageId?: number | undefined;
  // }): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     this.promptQueue.push({ agent, message, enableSpeech, messageId, resolve, reject });
  //     this.processQueue();
  //   });
  // }

  // /**
  //  * Process the prompt queue sequentially.
  //  */
  // private async processQueue() {
  //   // If already processing a prompt, exit to prevent parallel processing
  //   if (this.isProcessing) return;

  //   // If the queue is empty, nothing to process
  //   if (this.promptQueue.length === 0) return;

  //   // Mark that processing has started
  //   this.isProcessing = true;

  //   // Dequeue the first prompt in the queue
  //   const { agent, message, enableSpeech, messageId, resolve, reject } = this.promptQueue.shift()!;

  //   try {
  //     // Send the prompt and await its completion
  //     await this.sendPrompt({
  //       agent,
  //       message,
  //       enableSpeech,
  //       messageId,
  //       onEndCallback: () => {
  //         // Mark processing as done
  //         this.isProcessing = false;
  //         // Resolve the promise associated with this prompt
  //         resolve();
  //         // Recursively process the next prompt in the queue
  //         this.processQueue();
  //       },
  //     });
  //   } catch (error) {
  //     console.error("Error processing prompt:", error);
  //     // Mark processing as done even if there was an error
  //     this.isProcessing = false;
  //     // Reject the promise associated with this prompt
  //     reject(error);
  //     // Continue processing the next prompt in the queue
  //     this.processQueue();
  //   }
  // }

  public sendPrompt = async ({
    agent,
    message,
    onEndCallback,
    enableSpeech = false,
    messageId
  }: {
    agent: Agent;
    message: string;
    onEndCallback?: (arg: any) => void;
    enableSpeech?: boolean;
    messageId?: number;
  }) => {
    this.currentPromptId = messageId;
    const aiEntity = agent.el;
    let leftoverText = ""; // Buffer to store leftover text
    let prompt = ""; // Full prompt text
    const charactersLimit = 80; // Word limit for applying cleaning
    let firstChunkProcessed = false; // Track if the first chunk has been processed

    this.audioManager.currentAgent = agent;
    window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_THINKING));
    agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_THINKING));

    // Create an AbortController for this prompt
    const controller = new AbortController();

    // Store the controller to allow cancellation
    this.currentAbortController = controller;

    // Flag to track cancellation
    isCancelled = false;

    let retrying = false;

    this.bufferKey = this.bufferKey + 1;
    const bufferKey = this.bufferKey;

    const processChunksToSpeech = (chunk: string, messageId?: number) => {
      // If speech is enabled, queue it for synthesis
      if (enableSpeech) {
        this.dispatchStartSpeaking(agent);
        voiceSynthesizer.agent = agent;
        voiceSynthesizer.addToQueue(chunk, true, messageId, messageId);
        // window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));
      }
    };

    const onEnd = async () => {
      if (isCancelled) {
        return;
      }

      if (!prompt && !retrying) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: "There was an error processing the prompt",
          receiver: agent.id,
          audio: false,
        });
        return;
      }

      const autoplay = enableSpeech && document.hasFocus() && this.currentPromptId === messageId;

      // Process any leftover text
      if (leftoverText.trim().length > 0 && agent.voiceSpeechStrategy === "OpenAI") {
        await this.audioManager.playStreamedAudio({
          signal: controller.signal,
          agent: agent,
          prompt: leftoverText.trim(),
          autoplay: autoplay,
          initialAudio: !firstChunkProcessed,
          finalAudio: true,
          bufferKey
        });
        this.audioManager.clearSessionChunks();
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(prompt),
          receiver: agent.id,
          audio: prompt && agent.audioBuffers ? { bufferKey } : undefined,
          play: autoplay
        });
      } else if (agent.voiceSpeechStrategy === "OpenAI" && prompt) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(prompt),
          receiver: agent.id,
          audio: prompt && agent.audioBuffers ? { bufferKey } : undefined
        });
      }

      // Process any leftover text
      if (leftoverText.trim().length > 0 && agent.voiceSpeechStrategy === "WebAPI" && bufferKey === this.bufferKey) {
        processChunksToSpeech(leftoverText.trim(), messageId);
      }

      if (agent.voiceSpeechStrategy === "WebAPI" && prompt) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(prompt),
          receiver: agent.id,
          audio: true,
          play: false,
          loadingAudio: autoplay
        });
      } else if (prompt) {
        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: prompt,
          receiver: agent.id
        });
      }

      window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_THINKING));

      if (onEndCallback) {
        onEndCallback(prompt);
      }
    };

    const onMessageChunk = async (chunk: { type: string; data: string }) => {
      if (isCancelled) {
        // Ignore any incoming chunks if cancelled
        return;
      }

      let chunkData = chunk.data;
      if (chunk.type === "text" && chunk.data) {
        // Handle regular text chunk
        chunkData = sanitizeAIMessage(chunk.data);
        // chunkData = mergeCompoundWords(chunk.data, compoundWordsMap);
        prompt += chunkData;

        this.sendThreadMessageAI({
          id: messageId,
          el: aiEntity,
          message: sanitizeAIMessage(prompt),
          receiver: agent.id
        });

        const combinedText = leftoverText + chunkData;
        const aggregatedChunks = gracefulChunkingWithLimit(combinedText, charactersLimit);
        if (agent.voiceSpeechStrategy === "WebAPI") {
          if (!firstChunkProcessed && aggregatedChunks[0].length >= charactersLimit) {
            firstChunkProcessed = true;
            leftoverText += chunkData;
            // processChunksToSpeech(aggregatedChunks[0]);
            // firstChunkProcessed = true;
            // leftoverText = aggregatedChunks.slice(1).join("");
          } else {
            leftoverText += chunkData;
          }
        } else if (agent.voiceSpeechStrategy == "OpenAI") {
          if (!firstChunkProcessed && aggregatedChunks[0].length >= charactersLimit) {
            firstChunkProcessed = true;
            leftoverText += chunkData;
            // leftoverText = aggregatedChunks.slice(1).join("");
            // await this.audioManager.playStreamedAudio({
            //   signal: controller.signal,
            //   agent: agent,
            //   prompt:aggregatedChunks[0],
            //   autoplay: enableSpeech,
            //   initialAudio: true,
            //   finalAudio: false,
            //   bufferKey,
            // });
          } else {
            leftoverText += chunkData;
          }
        }
      } else if (chunk.type === "thread" && chunk.data) {
        this.updateAgent({ ...agent, threadId: chunk.data });
      }
    };

    const onError = (error: string) => {
      if (isCancelled) {
        return;
      }

      this.sendThreadMessageAI({
        id: messageId,
        el: aiEntity,
        message: `There was an error processing the prompt. ${error}`,
        receiver: agent.id,
        audio: false,
        play: false,
        loadingAudio: false,
        playable: false
      });
    };

    const onRetry = () => {
      retrying = true;
    };

    this.cancelAllPlay(agent, false);

    await sendThreadPrompt({
      prompt: message,
      thread: agent?.threadId,
      onEnd,
      onMessageChunk,
      onError,
      controller: this.currentAbortController,
      onRetry,
      uuid: agent.objectUuid,
    });
    // // Cleanup after prompt is processed
    // this.currentAbortController = null;
  };

  /**
   * Cancel the currently processing prompt.
   */
  public async cancelCurrentPrompt({ id, el }: { id: any; el: any }) {
    const currentAgent = this.getAgentById(id);

    if (this.currentAbortController) {
      // Mark the prompt as cancelled
      isCancelled = true;
      // Abort the SSE connection
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }

    if (currentAgent?.voiceSpeechStrategy === "WebAPI") {
      voiceSynthesizer.queue = [];
      voiceSynthesizer.agent = null;
      voiceSynthesizer.isSpeaking = false;
      voiceSynthesizer.stop();
      // await new Promise((resolve) => setTimeout(resolve, 300 ));
    }

    const AILastMessage = (window as any).getAILastMessage(id, true);

    if (AILastMessage && AILastMessage.loading) {
      const messageId = AILastMessage.id;
      window.APP.messageDispatch.dispatchEvent(
        new CustomEvent("ai-remove-message", {
          detail: { id: AILastMessage.id, ...AILastMessage }
        })
      );
    }

    const AILastMessageWithoutLoading = (window as any).getAILastMessage(id, false);

    const textMessage = AILastMessageWithoutLoading?.messages[0].body;

    const audio = AILastMessageWithoutLoading?.messages[0]?.audio;

    if (
      AILastMessageWithoutLoading &&
      currentAgent &&
      currentAgent?.voiceSpeechStrategy != "Silent" &&
      AILastMessageWithoutLoading.messages[0] &&
      !audio
    ) {
      this.bufferKey = this.bufferKey + 1;
      const bufferKey = this.bufferKey;

      await this.audioManager.playStreamedAudio({
        agent: currentAgent,
        prompt: textMessage,
        autoplay: false,
        initialAudio: false,
        finalAudio: true,
        bufferKey
      });
      this.audioManager.clearSessionChunks();
      this.sendThreadMessageAI({
        id: AILastMessageWithoutLoading.id,
        el,
        message: sanitizeAIMessage(textMessage),
        receiver: id,
        audio: { bufferKey }
      });
    }

    // Stop audio playback if any
    if (currentAgent && currentAgent.currentAudio && currentAgent.voiceSpeechStrategy === "OpenAI") {
      currentAgent.currentAudio.pause();
      currentAgent.currentAudio.currentTime = 0;

      if (currentAgent.currentAudio.onended) {
        currentAgent.currentAudio.onended(new Event("ended"));
      }
    }

    // Dispatch events to notify other parts of the application
    this.cancelPreviousAudiosPlayStatus();

    // Clear any session audio chunks if necessary
    this.audioManager.isPlaying = false;
    this.audioManager.clearSessionChunks();
  }

  public async cancelAllPlay(agent: Agent, aiFinishSpeak: any = true) {
    voiceSynthesizer.agent = null;
    voiceSynthesizer.isSpeaking = false;
    voiceSynthesizer.queue = [];
    voiceSynthesizer.stop();
    window.APP.messageDispatch.dispatchEvent(new CustomEvent("ai-cancel-play"));

    if (aiFinishSpeak) {
      window.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }

    if (!agent) {
      return;
    }

    if (!agent.currentAudio.paused) {
      agent.el.dispatchEvent(new CustomEvent(AGENT_EVENTS.AI_FINISH_SPEAK));
    }

    try {
      if (agent && agent.voiceSpeechStrategy === "OpenAI") {
        agent.currentAudio.pause();
      }
    } catch (_) {}
  }

  public async getOpenAITranscriptionByAudio(audioChunks: any, isBuffer: boolean, uuid: string) {
    let audioBlob;
    if (isBuffer) {
      audioBlob = new Blob([audioChunks], { type: "audio/mp3" });
    } else {
      audioBlob = new Blob(audioChunks, { type: "audio/mp3" });
    }

    try {
      // Pass uuid instead of apiKey to sendAudioTranscription
      const promptTranscribed = await sendAudioTranscription({
        audio: audioBlob,
        signal: this.currentAbortController?.signal,
        uuid
      }); // string response
      return promptTranscribed;
    } catch (error) {
      console.error("Error in transcription", error);
      return null;
    }
  }

  private playAudioUrl(agent: Agent, url: string) {
    agent.currentAudio.src = url;
    agent.currentAudio.play();
  }
}

// Exporting a singleton instance
const aiAgents = AIAgents.getInstance();
declare global {
  interface Window {
    aiAgents: any;
  }
}

window.aiAgents = aiAgents;

export default aiAgents;
